<template>
  <div class="event-form" v-if="newEvent">
    <section>
      <div class="event-form--section-title">
        <span>Description</span>
      </div>
      <div class="event-form--content">
        <div class="event-form--row event-form--row-flex">
          <InputText name="name" v-model="newEvent.name" label="Event Name *" />

          <InputSelect
            v-if="accountsStore.accountsDataRequired"
            name="account"
            v-model="newEvent.account_id"
            :filter="true"
            :options="accountsStore.accountsDataRequired"
            optionValue="id"
            optionLabel="display_name"
            @optionSelected="onChangeAccount"
            :disabled="!editable"
            :hasIconLeft="true"
            label="Account *"
          >
            <template #icon>
              <IconAccount />
            </template>
          </InputSelect>
        </div>

        <div class="event-form--row" v-if="eventsStore.eventList">
          <InputSelect
            name="location"
            v-model="newEvent.timezone_id"
            :filter="true"
            :options="eventsStore.eventList.location"
            optionValue="id"
            optionLabel="name"
            :hasIconLeft="true"
            label="Event Location *"
          >
            <template #icon>
              <IconLocation />
            </template>
          </InputSelect>
        </div>
      </div>
    </section>

    <section v-if="newEvent.canHaveBooking">
      <div class="event-form--section-title">
        <span>Booking</span>
        <div>
          Select a valid booking
          <Tooltip position="top">
            <template v-slot:text> <IconInfo /> </template>
            <template v-slot:tooltip>
              Ensure that your account has a valid ad-hoc booking or sufficient credits before
              proceeding further.
            </template>
          </Tooltip>
        </div>
      </div>
      <div class="event-form--content">
        <div class="event-form--row event-form--row-flex event-form--row-booking">
          <label>Booking Name</label>

          <div v-if="!bookings || !bookings.length" class="no-bookings">
            <span>You don’t have any bookings available</span>
            <RouterLink to="/events/purchasing"> Create Booking <IconExternalLink /> </RouterLink>
          </div>

          <InputSelect
            v-else
            name="account"
            v-model="newEvent.booking_id"
            :filter="true"
            :options="bookings"
            optionValue="id"
            optionLabel="campaign_name"
            @optionSelected="setBooking"
            label="Select booking *"
          />
        </div>
      </div>
    </section>

    <section>
      <!-- TODO (API Limitation): This should be a multi-pick like Verticals -->
      <div class="event-form--section-title">
        <span>Event Types</span>
        <div>
          pick one
          <Tooltip position="left">
            <template v-slot:text>
              <IconInfo />
            </template>
            <template v-slot:tooltip>
              Pick an event type that most closely matches your event.
            </template>
          </Tooltip>
        </div>
      </div>
      <div class="event-form--content event-form--content-flex">
        <Tooltip
          position="bottom"
          v-for="type in eventsStore.eventList?.event_type"
          :key="type.id"
          :hasTooltip="getTooltip(type.name) !== ''"
        >
          <template v-slot:text>
            <Chip
              :key="type.id"
              :isToggle="true"
              :colored="newEvent.type === type.id"
              @chipClicked="toggleType(type.id)"
            >
              {{ type.name }}
            </Chip>
          </template>
          <template v-slot:tooltip>
            {{ getTooltip(type.name) }}
          </template>
        </Tooltip>
      </div>
    </section>

    <section>
      <div class="event-form--section-title">
        <span>Verticals</span>
        <div>
          one or multiple
          <Tooltip position="left">
            <template v-slot:text>
              <IconInfo />
            </template>
            <template v-slot:tooltip>
              Pick one or more industry or vertical that your event comes under. Verticals help in
              benchmarking and contextualising event metrics.
            </template>
          </Tooltip>
        </div>
      </div>
      <div class="event-form--content event-form--content-flex">
        <Chip
          v-for="vert in eventsStore.eventList?.vertical"
          :key="vert.id"
          :isToggle="true"
          :colored="newEvent.verticals.includes(vert.id)"
          :canDelete="newEvent.verticals.includes(vert.id)"
          @chipClicked="toggleVertical(vert.id)"
        >
          {{ vert.name }}
        </Chip>
      </div>
    </section>

    <section v-if="userStore.isStaff && newEvent.options && newEvent.account_id">
      <div class="event-form--section-title">
        <span>Event Options</span>
      </div>
      <div class="event-form--content">
        <div class="form-row form-row--full">
          <div class="form-cell">
            <InputCheckbox
              name="hide_metrics"
              v-model="newEvent.options.hide_metrics"
              :checked="newEvent.options.hide_metrics"
              @click="onToggleHideMetrics"
              label="Hide the event on the dashboard"
            />
          </div>
        </div>
        <div class="form-row form-row--full">
          <div class="form-cell">
            <InputSelect
              v-model="newEvent.options.removal_event"
              label="Staff Removal Event"
              :options="removeEventList"
              optionLabel="name"
              optionValue="uuid"
            />
          </div>
        </div>

        <div class="event-form--section-title">
          <span>Calibration</span>
        </div>

        <div class="form-row" v-if="newEvent.options?.calibration">
          <div class="form-cell">
            <InputNumber
              name="multiplier_engaged"
              v-model="newEvent.options.calibration.engaged"
              :step="0.01"
              :min="0.01"
              mask="##.##"
              label="Engaged"
            />
          </div>

          <div class="form-cell">
            <InputNumber
              name="multiplier_passerby"
              v-model="newEvent.options.calibration.passerby"
              :step="0.01"
              :min="0.01"
              mask="##.##"
              label="Passerby"
            />
          </div>

          <div class="form-cell">
            <InputNumber
              name="multiplier_other"
              v-model="newEvent.options.calibration.other"
              :step="0.01"
              :min="0.01"
              mask="##.##"
              label="Other"
            />
          </div>
        </div>

        <div class="event-form--section-title">
          <span>Auto-Cloning</span>
        </div>

        <div class="event-row--radio-button" v-if="newEvent.options.cloning">
          <RadioButton
            v-model="newEvent.options.cloning.type"
            inputId="cloningNone"
            name="cloning"
            value="None"
          />
          <label for="cloningNone">No Automatic Cloning</label>
        </div>
        <div class="event-row--radio-button" v-if="newEvent.options.cloning">
          <RadioButton
            v-model="newEvent.options.cloning.type"
            inputId="cloningWeekly"
            name="cloning"
            value="Weekly"
          />
          <label for="cloningWeekly">Repeats each week</label>
        </div>

        <div v-if="newEvent.options.cloning?.type === 'Weekly'" class="event-row--radio-select">
          <InputSelect
            name="location"
            v-model="newEvent.options.cloning.format"
            :options="cloningFormats"
            optionValue="id"
            optionLabel="label"
            label="Event Naming Format"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import dayjs from 'dayjs';
import { RadioButton } from 'primevue';

import InputCheckbox from '@/components/inputs/InputCheckbox.vue';
import InputNumber from '@/components/inputs/InputNumber.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';

import Chip from '@/components/global/Chip.vue';
import Tooltip from '@/components/global/Tooltip.vue';

import IconAccount from '@/icons/users/IconAccount.svg?component';
import IconExternalLink from '@/icons/IconExternalLink.svg?component';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconLocation from '@/icons/IconLocation.svg?component';

import type { EventList, EventIndexQuery, EventTypeNew } from '@/types/Event';
import type { AccountBooking } from '@/types/Booking';
import { getAccountById, getEvents, getAvailableEventBookings, getEventBooking } from '@/utils/api';

import { useAccountsStore } from '@/stores/accounts';
const accountsStore = useAccountsStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

const newEvent = defineModel<EventTypeNew>('newEvent');
const removeEventList = ref<EventList[]>([]);
const bookings = ref<AccountBooking[]>([]);

const props = defineProps<{
  isEditMode: boolean;
}>();

const editable = ref(true);

if (userStore.isNonStaff && userStore.userData?.account_id && newEvent.value) {
  newEvent.value.account_id = userStore.userData.account_id;
  editable.value = false;
}

const toggleType = (typeId: number) => {
  if (newEvent.value) {
    newEvent.value.type = typeId;
    // if (newEvent.value.types.includes(typeId)) {
    //   newEvent.value.types.splice(newEvent.value.types.indexOf(typeId), 1);
    // } else {
    //   newEvent.value.types.push(typeId);
    // }
  }
};

const toggleVertical = (vertId: number) => {
  if (newEvent.value) {
    if (newEvent.value.verticals.includes(vertId)) {
      newEvent.value.verticals.splice(newEvent.value.verticals.indexOf(vertId), 1);
    } else {
      newEvent.value.verticals.push(vertId);
    }
  }
};

const onChangeAccount = () => {
  if (newEvent.value?.booking_id) {
    newEvent.value.booking_id = undefined;
  }

  getEventOptionData();

  if (newEvent.value?.zones) {
    newEvent.value.zones = [];
  }

  if (newEvent.value?.options) {
    newEvent.value.options.removal_event = '';
  }
};

const onToggleHideMetrics = () => {
  if (newEvent.value?.options) {
    newEvent.value.options.hide_metrics = !newEvent.value?.options?.hide_metrics;
  }
};

const getTooltip = (type: string) => {
  let text = '';

  switch (type) {
    case 'Dealership':
      text = 'Car, boat or bike dealerships';
      break;
    case 'Exhibition':
      text = 'Trade shows and Conferences';
      break;
    case 'Fanzone / Festival':
      text =
        'Designated areas for multiple partner activations at fairs, outdoor markets, live shows/concerts and large sporting events';
      break;
    case 'Indoor Brand Activation':
      text = 'Any non "Retail" or "Fanzone/Festival" indoor event';
      break;
    case 'Outdoor Brand Activation':
      text = 'Any non "Fanzone/Festival" outdoor event';
      break;
    case 'Retail':
      text = 'Shopping malls, large kiosks, shops';
      break;
    case 'Rightsholder':
      text = 'Sports organisations, Venue owners, Event organisers';
      break;
    default:
      text = '';
      break;
  }

  return text;
};

const cloningFormats = ref([
  {
    id: 'WK',
    label: 'Week Number (e.g.: Gateshead Shop WK11)',
  },
  {
    id: 'Y.M',
    label: 'Y.M (e.g.: Gateshead Shop 2025.02)',
  },
  {
    id: 'MDY',
    label: 'MM/DD/YYYY (e.g.: Gateshead Shop 02/28/2025)',
  },
  {
    id: 'YMD',
    label: 'YYYY.MM.DD (e.g.: Gateshead Shop 2025.02.28)',
  },
  {
    id: 'YMD2',
    label: 'YYYY.MM.DD (e.g.: Gateshead Shop 2025.02.28)',
  },
]);

const getEventOptionData = () => {
  if (newEvent.value?.account_id) {
    if (!props.isEditMode) {
      getAccountById(userStore.accessToken, newEvent.value?.account_id).then((resp) => {
        if (newEvent.value) {
          newEvent.value.canHaveBooking = resp.data.features.includes('event_booking');
          if (newEvent.value.canHaveBooking && newEvent.value.account_id) {
            getAvailableEventBookings(userStore.accessToken, newEvent.value.account_id).then(
              (resp) => {
                bookings.value = resp.data;
              },
            );
          }
        }

        if (newEvent.value?.options) {
          newEvent.value.options.calibration = {
            engaged: resp.data.account.multiplier_engaged,
            passerby: resp.data.account.multiplier_passerby,
            other: resp.data.account.multiplier_other,
          };
        }
      });

      if (newEvent.value.options) {
        newEvent.value.options.cloning = {
          type: 'None',
          format: '',
        };
      }
    } else {
      if (newEvent.value.id) {
        getEventBooking(userStore.accessToken, newEvent.value.account_id, newEvent.value.id).then(
          (resp) => {
            bookings.value = resp.data;
          },
        );
      }
    }

    // Get removal events, use event dates if we are editing
    let start;
    let end;
    if (newEvent.value?.event_start && newEvent.value?.event_end) {
      start = dayjs(newEvent.value.event_start).subtract(5, 'days').toDate();
      end = dayjs(newEvent.value.event_end).add(5, 'days').toDate();
    } else {
      start = dayjs().subtract(3, 'days').toDate();
      end = null;
    }

    const eventParams = ref<EventIndexQuery>({
      page: 1,
      results_per_page: 20,
      account: newEvent.value.account_id,
      from: start,
      to: end,
      sortDesc: true,
      sortBy: 'event_start',
    });

    getEvents(userStore.accessToken, eventParams.value).then((resp) => {
      removeEventList.value = resp.events.data;

      if (props.isEditMode) {
        nextTick(() => {
          if (newEvent.value?.options?.removal_event) {
            // Force refresh now that we have the list of options.
            newEvent.value.options.removal_event = `${newEvent.value.options.removal_event}`;
          }
        });
      }
    });
  }
};

const setBooking = (booking_id: number) => {
  if (newEvent.value) {
    newEvent.value.booking = bookings.value?.find((booking) => booking.id === booking_id);
  }
};

onMounted(() => {
  getEventOptionData();
});
</script>

<style lang="scss">
.event-row--radio-button {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxs);
  margin-bottom: variables.$vantage-base-sizing-xxs;
}

.event-row--radio-select {
  margin-left: variables.$vantage-base-sizing-lg;
  margin-top: variables.$vantage-base-sizing-lg;
}

.event-form--row-booking {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-sm);

  label {
    flex-shrink: 0;
  }

  .input-row {
    width: 100%;
  }
}

.no-bookings {
  @include mixins.flex-center($horiz: space-between);
  width: calc(100% - 150px);

  a {
    margin-left: auto;
  }
}
</style>
